import { useCallback, useLayoutEffect, useState } from 'react';
import { getCanUseBrowserDOM, getArrayLength } from 'utils';

const DEFAULT_ACTION_MENU_TOP = 90;
const TOAST_TO_ACTION_MENU_GAP = 8;
const TOAST_BANNER_SELECTOR = '.toast-banner.visible';

type UseToastPositionCalculationProps = {
  toastSelector?: string;
  defaultTopOffset?: number;
  gapBetweenElements?: number;
};

const getToastTopOffset = (
  selector: string,
  defaultTop: number,
  gap: number,
) => {
  const visibleToastBanners = Array.from(document.querySelectorAll(selector));

  if (!getArrayLength(visibleToastBanners)) return defaultTop;

  const maxBottom = Math.max(
    ...visibleToastBanners.map(
      (toastBanner) => toastBanner.getBoundingClientRect().bottom,
    ),
  );

  return Math.max(defaultTop, Math.ceil(maxBottom + gap));
};

const useToastPositionCalculate = ({
  toastSelector = TOAST_BANNER_SELECTOR,
  defaultTopOffset = DEFAULT_ACTION_MENU_TOP,
  gapBetweenElements = TOAST_TO_ACTION_MENU_GAP,
}: UseToastPositionCalculationProps = {}) => {
  const [position, setPosition] = useState({ top: defaultTopOffset });

  const updateTopOffset = useCallback(
    () =>
      setPosition({
        top: getToastTopOffset(
          toastSelector,
          defaultTopOffset,
          gapBetweenElements,
        ),
      }),
    [toastSelector, defaultTopOffset, gapBetweenElements],
  );

  useLayoutEffect(() => {
    if (!getCanUseBrowserDOM()) return;

    updateTopOffset();
  }, [updateTopOffset]);

  useLayoutEffect(() => {
    if (!getCanUseBrowserDOM()) return;

    const observer = new MutationObserver(updateTopOffset);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      observer.disconnect();
    };
  }, [updateTopOffset]);

  useLayoutEffect(() => {
    if (!getCanUseBrowserDOM()) return;

    window.addEventListener('resize', updateTopOffset);

    return () => {
      window.removeEventListener('resize', updateTopOffset);
    };
  }, [updateTopOffset]);

  return position;
};

export default useToastPositionCalculate;
